/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { useAppDispatch } from '@core/redux/hooks';
import { useSelector, shallowEqual } from 'react-redux';
import { viewer3dActions, viewer3dSelectors } from '@viewer3D/redux';
import { projectSelectors } from '@projects/redux';
import { useSnapshot } from 'valtio';
import { encodeScanFilters, useScanFilters } from '@projects/pages/detail/hooks';
import { scanThunks } from '@scans/redux';
import { useCallback, useEffect, useRef } from 'react';
import { QAState } from '@scans/types';
import { DateTime } from 'luxon';
import { trackUserEvent, UserEvent } from '@core/analytics';
import { unwrapResult } from '@reduxjs/toolkit';
import { errorBox } from '@core/components/AlertBox';
import type { RootState } from '@core/redux/interface';
import { useScansInScope } from '../hooks';
import {
  checkboxSelectionState,
  searchedScanIdState,
  setSearchedScanId,
} from './checkboxSelectionState';

export const useConnectScans = () => {
  const dispatch = useAppDispatch();
  const projectId = useSelector(viewer3dSelectors.selectedProjectId);
  const selectedScanId = useSelector(viewer3dSelectors.selectedScanId);
  const initialCameraSet = useSelector(viewer3dSelectors.initialCameraSet);
  const scans = useScansInScope();
  const { checkedScans } = useSnapshot(checkboxSelectionState);
  const scanFilters = useScanFilters();
  const utmZone = useSelector(projectSelectors.selectById(projectId))?.utmZone;
  const { searchedScanId } = useSnapshot(searchedScanIdState);

  const firstVisibleScan = scans.find((s) => s.visible !== false && s.projectId === projectId);
  const firstScanPoint = useSelector(
    (state: RootState) =>
      firstVisibleScan
        ? viewer3dSelectors.selectScanPointById(firstVisibleScan.id)(state)
        : undefined,
    shallowEqual,
  );

  const searchedScanPoint = useSelector(
    (state: RootState) =>
      searchedScanId ? viewer3dSelectors.selectScanPointById(searchedScanId)(state) : undefined,
    shallowEqual,
  );

  const loadSearchedScan = async (id: string) => {
    if (!id) return;
    setSearchedScanId(id.trim());
    const action = await dispatch(
      scanThunks.searchWithFilters({
        path: ['scans', 'search', 'withFilters'],
        params: {
          id: id.trim(),
          projection: 'projectDetail',
          projectId,
        },
      }),
    );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any
    const actualPayload = unwrapResult<any>(action);

    if (actualPayload.empty) return;

    if (actualPayload.content[0].projectId !== projectId) return;

    dispatch(viewer3dActions.setSelectedScanId({ id: id.trim(), focus: false }));
  };

  useEffect(() => {
    if (!searchedScanId) return;
    loadSearchedScan(searchedScanId);
  }, [searchedScanId]);

  const firstScanPointX = firstScanPoint?.x;
  const firstScanPointY = firstScanPoint?.y;
  const firstScanPointZ = firstScanPoint?.z;

  const searchedScanPointX = searchedScanPoint?.x;
  const searchedScanPointY = searchedScanPoint?.y;
  const searchedScanPointZ = searchedScanPoint?.z;

  const lastInitializedProjectIdRef = useRef<string | undefined>(undefined);
  const lastFocusedSearchedRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!searchedScanId) {
      lastFocusedSearchedRef.current = undefined;
      return;
    }
    if (
      selectedScanId === searchedScanId &&
      searchedScanPointX !== undefined &&
      lastFocusedSearchedRef.current !== searchedScanId
    ) {
      lastFocusedSearchedRef.current = searchedScanId;
      dispatch(viewer3dActions.focusSelectedEntity);
    }
  }, [
    searchedScanId,
    selectedScanId,
    searchedScanPointX,
    searchedScanPointY,
    searchedScanPointZ,
    dispatch,
  ]);

  useEffect(() => {
    if (
      projectId &&
      lastInitializedProjectIdRef.current !== projectId &&
      !initialCameraSet &&
      !selectedScanId &&
      firstScanPointX !== undefined &&
      firstScanPointY !== undefined &&
      firstScanPointZ !== undefined
    ) {
      lastInitializedProjectIdRef.current = projectId;
      dispatch(
        viewer3dActions.cameraGoto({
          x: firstScanPointX,
          y: firstScanPointY,
          z: firstScanPointZ,
          initial: true,
        }),
      );
    }
  }, [
    projectId,
    firstScanPointX,
    firstScanPointY,
    firstScanPointZ,
    selectedScanId,
    initialCameraSet,
    dispatch,
  ]);

  const loadScans = useCallback(() => {
    if (!scanFilters.viewport && !scanFilters.dateRange) {
      // prevent loading a lot of scans when there are no filters in place yet
      return;
    }
    const encodedFilters = encodeScanFilters(scanFilters);

    dispatch(
      scanThunks.getMany({
        path: ['scans', 'search', 'withFilters'],
        params: {
          projectId,
          scanCategory: scanFilters.scanCategory,
          ...encodedFilters,
          utmZone,
          projection: 'projectDetail',
          size: 1000,
        },
      }),
    );
  }, [scanFilters, projectId, utmZone, dispatch]);

  const updateScansQaState = async (
    checkedScans: typeof checkboxSelectionState.checkedScans,
    nextQaState: QAState,
    remarksMap: Record<string, string | null>,
  ): Promise<boolean> => {
    try {
      await dispatch(
        scanThunks.updateQaState({
          path: ['projects', projectId!, 'scans', 'qaState'],
          body: checkedScans.map((scan) => ({
            id: scan.id,
            qaState: nextQaState,
            reworkCategories: nextQaState === 'TO_REWORK' ? scan.reworkCategories : undefined,
            remarks: remarksMap[scan.id],
          })),
        }),
      ).unwrap();
      if (nextQaState === 'DONE') {
        // Note: This event is fired EVERY TIME a scan is set to "DONE".
        // You could set the scan to "DONE" then back to "QA_READY"/"TO_LABEL" and then back to "DONE" again etc.
        checkedScans.forEach((scan) => {
          if (!scan.uploadedAt) return;
          const diff = DateTime.fromISO(scan.uploadedAt).diffNow('hours');
          trackUserEvent(UserEvent.ScanFinished, {
            scanId: scan.id,
            hoursInProgress: Math.abs(diff.hours),
            projectId: projectId!,
          });
        });
      }
      return true;
    } catch (_error) {
      errorBox('Failed to update scans QA state. Check the scan statuses before retrying.');
      return false;
    }
  };

  return {
    loadScans,
    loadSearchedScan,
    scans,
    selectedScanId,
    checkedScanIds: checkedScans.map((s) => s.id),
    updateScansQaState,
  };
};
